// hooks/api/internship.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as internshipService from "@/services/internship.service";
import {
  InternshipCreateType,
  InternshipParamsType,
  InternshipType,
} from "@/services/internship.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const internshipsKey = {
  all: ["internships"] as const,
  lists: (filters: InternshipParamsType) =>
    [...internshipsKey.all, "lists", filters] as const,
  detail: (id: string) => [...internshipsKey.all, "detail", id] as const,
};

export const useInternshipsQuery = (filters: InternshipParamsType) =>
  useQuery({
    queryKey: internshipsKey.lists(filters),
    queryFn: () => internshipService.fetchAllInternships(filters),
    placeholderData: (prev) => prev,
  });

export const useInternshipByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: internshipsKey.detail(id),
    queryFn: () => internshipService.fetchInternshipById(id),
    enabled: enabled && !!id,
  });

export const useCreateInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();
  return useMutation<InternshipType, AxiosError, InternshipCreateType>({
    mutationFn: (data) => internshipService.createInternship(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      notifySuccess("Internship added successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to add internship"),
  });
};

export const useUpdateInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: InternshipCreateType }) =>
      internshipService.updateInternship(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: internshipsKey.detail(variables._id),
      });
      notifySuccess("Internship updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update internship"
      ),
  });
};

export const useDeleteInternshipMutation = (filters: InternshipParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => internshipService.deleteInternship(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: internshipsKey.lists(filters),
      });
      queryClient.removeQueries({ queryKey: internshipsKey.detail(_id) });
      notifySuccess("Internship deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete internship"
      ),
  });
};
